import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Eye, EyeOff, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Modal bloccante mostrato quando `user.must_change_password === true`.
 * L'utente non può accedere al resto dell'app finché non cambia la password.
 * Usato tipicamente al primo login dopo il reset produzione o dopo reset amministrativo.
 */
export default function MustChangePasswordModal() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);

  if (!user?.must_change_password) return null;

  const rules = [
    { ok: next.length >= 10, label: "Almeno 10 caratteri" },
    { ok: /[A-Z]/.test(next), label: "Almeno una MAIUSCOLA" },
    { ok: /[a-z]/.test(next), label: "Almeno una minuscola" },
    { ok: /\d/.test(next), label: "Almeno un numero" },
    { ok: /[^A-Za-z0-9]/.test(next), label: "Almeno un simbolo" },
    { ok: next.length > 0 && next !== current, label: "Diversa dalla temporanea" },
  ];
  const allOk = rules.every((r) => r.ok) && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password aggiornata — puoi iniziare a lavorare");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="must-change-password-modal"
      className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
    >
      <div className="bg-card rounded-3xl border border-border max-w-md w-full p-6 md:p-8 shadow-2xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-destructive/15 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-6 h-6 text-destructive" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-serif-display text-2xl leading-tight">Cambia password</div>
            <p className="text-xs text-muted-foreground mt-1">
              Per motivi di sicurezza devi impostare una nuova password prima di continuare.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Password corrente */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary block mb-1">Password temporanea</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <input
                data-testid="cp-current"
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full pl-11 pr-11 py-2.5 rounded-full border border-input bg-card text-sm"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
              </button>
            </div>
          </div>

          {/* Nuova password */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary block mb-1">Nuova password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <input
                data-testid="cp-new"
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full pl-11 pr-11 py-2.5 rounded-full border border-input bg-card text-sm"
              />
              <button
                type="button"
                onClick={() => setShowNext(!showNext)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted"
              >
                {showNext ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
              </button>
            </div>
          </div>

          {/* Conferma */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary block mb-1">Ripeti nuova password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <input
                data-testid="cp-confirm"
                type={showNext ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full pl-11 pr-4 py-2.5 rounded-full border border-input bg-card text-sm"
              />
            </div>
            {confirm && confirm !== next && (
              <p className="text-[10px] text-destructive mt-1">Le due password non coincidono</p>
            )}
          </div>

          {/* Requisiti password */}
          <div className="p-3 rounded-2xl bg-muted/40 border border-border" data-testid="cp-rules">
            <div className="text-[10px] uppercase tracking-widest text-secondary mb-2">Requisiti</div>
            <ul className="space-y-1">
              {rules.map((r, i) => (
                <li key={i} className={`text-xs flex items-center gap-2 ${r.ok ? "text-secondary" : "text-muted-foreground"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${r.ok ? "bg-secondary" : "bg-muted-foreground/50"}`} />
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          <button
            data-testid="cp-submit"
            type="submit"
            disabled={busy || !allOk}
            className="w-full py-3 rounded-full bg-primary text-primary-foreground text-sm uppercase tracking-widest btn-lift disabled:opacity-40"
          >
            {busy ? "Aggiornamento…" : "Imposta nuova password"}
          </button>
        </form>
      </div>
    </div>
  );
}
